import React, { useState } from 'react';
import api from '@/api/api';
import { SupplierOfferForm as SupplierOfferData, SupplyAnswer } from '@/models/customersTypes';
import FormField, {
    EMAIL_INVALID_TEXT,
    EMAIL_REGEX,
    REQUIRED_TEXT,
    SEND_FAILED_TEXT,
    SHORT_MAX_LENGTH,
    SubmitButton,
    TEXT_MAX_LENGTH,
    inputClass,
} from '@/components/Forms/FormField';
import { track } from '@/helpers/analytics';

const SUPPLY_ANSWERS: SupplyAnswer[] = ['Ja', 'Nein', 'Nach Absprache'];

const EMPTY_FORM: SupplierOfferData = {
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    location: '',
    products: '',
    delivery_area: '',
    delivery_schedule: '',
    self_delivery: '',
    self_stocking: '',
    remarks: '',
};

const REQUIRED: (keyof SupplierOfferData)[] = [
    'company_name',
    'contact_name',
    'email',
    'location',
    'products',
    'delivery_area',
    'delivery_schedule',
    'self_delivery',
    'self_stocking',
];

type TextKey = Exclude<keyof SupplierOfferData, 'self_delivery' | 'self_stocking'>;

const TEXT_FIELDS: { key: TextKey; label: string; type?: string; multiline?: boolean }[] = [
    { key: 'company_name', label: 'Dein Betrieb' },
    { key: 'contact_name', label: 'Dein Name' },
    { key: 'email', label: 'E-Mail-Adresse', type: 'email' },
    { key: 'phone', label: 'Telefonnummer (optional)', type: 'tel' },
    { key: 'website', label: 'Website (optional)', type: 'url' },
    { key: 'location', label: 'Ort / Postleitzahl' },
    { key: 'products', label: 'Welche Produkte möchtest du anbieten?', multiline: true },
    { key: 'delivery_area', label: 'Welche Standorte oder welche Region kannst du beliefern?', multiline: true },
    { key: 'delivery_schedule', label: 'An welchen Tagen und wie häufig kannst du liefern?' },
];

function validate(form: SupplierOfferData) {
    const errors: Record<string, string> = {};
    REQUIRED.forEach((key) => {
        if (!form[key].trim()) errors[key] = REQUIRED_TEXT;
    });
    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) errors.email = EMAIL_INVALID_TEXT;
    return errors;
}

const FIELD_ORDER: (keyof SupplierOfferData)[] = [...TEXT_FIELDS.map(({ key }) => key), 'self_delivery', 'self_stocking'];

function focusFirstError(errors: Record<string, string>) {
    const key = FIELD_ORDER.find((k) => errors[k]);
    if (!key) return;
    const field = document.getElementById(`offer-${key}`) ?? document.querySelector<HTMLInputElement>(`input[name="${key}"]`);
    field?.focus();
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

const SupplierOfferForm: React.FC = () => {
    const [form, setForm] = useState<SupplierOfferData>(EMPTY_FORM);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

    const update = <K extends keyof SupplierOfferData>(key: K, value: SupplierOfferData[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationErrors = validate(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            focusFirstError(validationErrors);
            return;
        }

        setStatus('sending');
        try {
            await api.interests.sendSupplierOffer(form);
            track('supplier-offer-sent', { self_delivery: form.self_delivery, self_stocking: form.self_stocking });
            setStatus('sent');
            setForm(EMPTY_FORM);
        } catch {
            track('form-error', { form: 'supplier-offer' });
            setStatus('error');
        }
    };

    if (status === 'sent') {
        return (
            <div className="rounded-2xl bg-white p-6 shadow-md" role="status">
                <h3 className="mb-2 text-2xl font-bold">Danke für deine Anfrage.</h3>
                <p>Deine Angaben sind bei uns eingegangen. Wir prüfen, ob dein Angebot zu unseren Standorten passt.</p>
            </div>
        );
    }

    const supplyQuestion = (key: 'self_delivery' | 'self_stocking', label: string) => (
        <fieldset>
            <legend className="mb-1 font-semibold">
                {label}<span aria-hidden="true">*</span>
            </legend>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
                {SUPPLY_ANSWERS.map((answer) => (
                    <label key={answer} className="inline-flex items-center gap-2">
                        <input
                            type="radio"
                            name={key}
                            value={answer}
                            checked={form[key] === answer}
                            onChange={() => update(key, answer)}
                            className="h-4 w-4 accent-primaryDark"
                        />
                        {answer}
                    </label>
                ))}
            </div>
            {errors[key] && <p className="mt-1 text-sm text-red-600">{errors[key]}</p>}
        </fieldset>
    );

    return (
        <form onSubmit={handleSubmit} noValidate className="space-y-5 rounded-2xl bg-white p-6 shadow-md" data-testid="supplier-offer-form">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {TEXT_FIELDS.map(({ key, label, type = 'text', multiline }) => (
                    <div key={key} className={multiline ? 'md:col-span-2' : ''}>
                        <FormField label={label} htmlFor={`offer-${key}`} required={REQUIRED.includes(key)} error={errors[key]}>
                            {multiline ? (
                                <textarea
                                    id={`offer-${key}`}
                                    rows={3}
                                    value={form[key]}
                                    onChange={(e) => update(key, e.target.value)}
                                    maxLength={TEXT_MAX_LENGTH}
                                    className={inputClass}
                                />
                            ) : (
                                <input
                                    id={`offer-${key}`}
                                    type={type}
                                    value={form[key]}
                                    onChange={(e) => update(key, e.target.value)}
                                    maxLength={SHORT_MAX_LENGTH}
                                    className={inputClass}
                                />
                            )}
                        </FormField>
                    </div>
                ))}
            </div>

            {supplyQuestion('self_delivery', 'Kannst du selbst anliefern?')}
            {supplyQuestion('self_stocking', 'Kannst du deine Produkte vor Ort selbst einräumen?')}

            <FormField label="Was sollten wir noch wissen? (optional, z. B. Kühlung oder Haltbarkeit)" htmlFor="offer-remarks">
                <textarea
                    id="offer-remarks"
                    rows={3}
                    value={form.remarks}
                    onChange={(e) => update('remarks', e.target.value)}
                    maxLength={TEXT_MAX_LENGTH}
                    className={inputClass}
                />
            </FormField>

            <p className="text-sm text-gray-600">
                * Pflichtfelder. Wir verwenden deine Angaben nur, um deine Anfrage zu bearbeiten. Mehr dazu in unserer{' '}
                <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
            </p>

            {status === 'error' && <p className="text-red-600" role="alert">{SEND_FAILED_TEXT}</p>}

            <SubmitButton sending={status === 'sending'}>Lieferantenanfrage senden</SubmitButton>
        </form>
    );
};

export default SupplierOfferForm;
